import type { z } from 'zod';
import { useAuthStore } from '@/shared/store/auth';

/**
 * Cliente fetch del frontend.
 *
 * Modelo de autenticación:
 *   - El backend emite el access token (JWT, TTL 5min) en el header
 *     `x-access-token` tanto en login como en refresh.
 *   - El refresh token (TTL 30min) vive en una cookie httpOnly firmada
 *     `datazo_rt` (path=/api/auth) — el JS nunca la ve.
 *   - Mantenemos el access token SOLO en memoria (zustand sin persist) para
 *     evitar la superficie XSS de localStorage/sessionStorage.
 *   - Cada request adjunta `Authorization: Bearer <token>` si hay token.
 *   - Cualquier respuesta del backend con un header `x-access-token` actualiza
 *     el token en el store (captura implícita de refrescos).
 *   - En 401 intentamos UNA vez `/api/auth/refresh` (usa la cookie). Si el
 *     refresh devuelve un nuevo access, reintentamos el request original. Si
 *     falla → emitimos `auth:expired` para que la guard redirija a /login.
 */

export interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  parseAs?: z.ZodTypeAny;
  noCredentials?: boolean;
  /** Uso interno: evitar bucles de refresh. */
  _isRetry?: boolean;
  /** Uso interno: evitar adjuntar Authorization en /auth/login y /auth/refresh. */
  _skipAuth?: boolean;
}

export class ApiClientError extends Error {
  public readonly status: number;
  public readonly payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.payload = payload;
  }
}

export const AUTH_EXPIRED_EVENT = 'auth:expired';

function emitAuthExpired(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
  }
}

/** Lee el header `x-access-token` y lo persiste en el store si existe. */
function captureAccessToken(response: Response): void {
  const token = response.headers.get('x-access-token');
  if (token !== null && token.length > 0) {
    useAuthStore.getState().setAccessToken(token);
  }
}

async function rawFetch(path: string, options: FetchOptions): Promise<Response> {
  const { body, headers, noCredentials, _skipAuth, _isRetry, parseAs, ...rest } = options;
  void parseAs;
  void _isRetry;

  const finalHeaders: Record<string, string> = { Accept: 'application/json' };
  if (headers !== undefined) {
    if (headers instanceof Headers) {
      headers.forEach((value, key) => {
        finalHeaders[key] = value;
      });
    } else if (Array.isArray(headers)) {
      for (const [key, value] of headers) {
        if (key !== undefined && value !== undefined) finalHeaders[key] = value;
      }
    } else {
      Object.assign(finalHeaders, headers);
    }
  }

  // Authorization Bearer si tenemos token (excepto en login/refresh).
  if (_skipAuth !== true) {
    const token = useAuthStore.getState().accessToken;
    if (token !== null && token.length > 0 && finalHeaders.Authorization === undefined) {
      finalHeaders.Authorization = `Bearer ${token}`;
    }
  }

  let serialized: BodyInit | undefined;
  if (body !== undefined) {
    if (body instanceof FormData) {
      serialized = body;
    } else {
      finalHeaders['Content-Type'] = 'application/json';
      serialized = JSON.stringify(body);
    }
  }

  return fetch(path, {
    ...rest,
    headers: finalHeaders,
    body: serialized,
    credentials: noCredentials === true ? 'omit' : 'include',
  });
}

/**
 * Intenta refrescar el access token. Devuelve `true` si el backend emitió un
 * nuevo token (capturado automáticamente por `captureAccessToken`), `false`
 * si no hubo refresh válido (cookie expirada, etc.).
 */
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  // Single-flight: si ya hay un refresh en curso, TODOS los llamadores esperan
  // el mismo. Sin esto, una página que dispara varias queries a la vez sin
  // access token (p. ej. abrir la impresión en una pestaña nueva, donde el
  // token vive solo en RAM y no se hereda) genera múltiples 401 → múltiples
  // POST /auth/refresh en paralelo. Como el backend ROTA el refresh token en
  // cada refresh, los refresh concurrentes se pisan: solo uno gana y los demás
  // reciben 401 → `emitAuthExpired()` → deslogueo de la sesión.
  if (refreshInFlight !== null) return refreshInFlight;
  refreshInFlight = (async (): Promise<boolean> => {
    try {
      const response = await rawFetch('/api/auth/refresh', {
        method: 'POST',
        _skipAuth: true,
        _isRetry: true,
      });
      if (!response.ok) return false;
      captureAccessToken(response);
      // Consumimos el body para liberar la conexión (no nos importa el contenido).
      try {
        await response.json();
      } catch {
        /* sin body o no JSON */
      }
      return useAuthStore.getState().accessToken !== null;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: FetchOptions = {},
): Promise<T> {
  const response = await rawFetch(path, options);
  captureAccessToken(response);

  // 401: si no es ya un reintento, probar refresh y reintentar UNA vez.
  if (response.status === 401 && options._isRetry !== true) {
    // Refresh y login no pueden re-intentar (evita ciclos).
    const isAuthEndpoint =
      path === '/api/auth/refresh' || path === '/api/auth/login';
    if (!isAuthEndpoint) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        return apiFetch<T>(path, { ...options, _isRetry: true });
      }
    }
    emitAuthExpired();
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const payload: unknown = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const message =
      isJson && payload !== null && typeof payload === 'object' && 'title' in payload
        ? String((payload as { title: unknown }).title)
        : `HTTP ${response.status}`;
    throw new ApiClientError(message, response.status, payload);
  }

  if (options.parseAs) {
    const parsed = options.parseAs.safeParse(payload);
    if (!parsed.success) {
      throw new ApiClientError(
        'Respuesta del servidor no cumple el contrato esperado',
        response.status,
        parsed.error.flatten(),
      );
    }
    return parsed.data as T;
  }

  return payload as T;
}

/**
 * Descarga un recurso binario (PDF, imagen, …) de un endpoint protegido.
 *
 * Un `<a href download>` plano NO sirve: el navegador no adjunta el header
 * `Authorization: Bearer` (el access token vive solo en memoria, no en cookie),
 * así que el endpoint responde 401 y el navegador guarda el cuerpo del error
 * como archivo → un PDF "dañado". Por eso hacemos el fetch autenticado a mano,
 * con el mismo flujo de refresh-en-401 que `apiFetch`, y devolvemos el Blob.
 */
export async function fetchBlob(
  path: string,
  options: FetchOptions = {},
): Promise<Blob> {
  let response = await rawFetch(path, options);
  captureAccessToken(response);

  if (response.status === 401 && options._isRetry !== true) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      response = await rawFetch(path, { ...options, _isRetry: true });
      captureAccessToken(response);
    }
  }

  if (!response.ok) {
    if (response.status === 401) emitAuthExpired();
    throw new ApiClientError(`HTTP ${response.status}`, response.status, null);
  }

  return response.blob();
}

/**
 * Descarga autenticada que dispara el guardado del archivo en el navegador.
 * Usa `fetchBlob` (envía el Bearer token) y un ObjectURL temporal.
 */
export async function downloadAuthenticated(
  path: string,
  filename: string,
): Promise<void> {
  const blob = await fetchBlob(path);
  const blobUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    a.rel = 'noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
  }
}
